import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  createWaiverOutlookDraft,
  fetchWaiverOutlookConfig,
} from '../../api/operationsClient'
import {
  WAIVER_TYPES,
  buildWaiverBody,
  defaultEncoreOptions,
  defaultEncoreValues,
  defaultExternalOptions,
  defaultExternalValues,
  defaultSubject,
  missingRequiredFields,
  openWaiverMailto,
  visibleFields,
} from '../../utils/snoop/waiverPage'

const inputClass =
  'w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm text-gray-900 shadow-sm focus:border-gray-400 focus:outline-none'
const labelClass = 'block text-xs font-medium text-gray-600'
const btnPrimary =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-gray-900 px-3.5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60'
const btnSecondary =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60'

/** Option toggles per waiver type, labelled for the UI. */
const ENCORE_TOGGLES = [
  { key: 'includeAlbumRights', label: 'Album rights' },
  { key: 'includeTrackRights', label: 'Track rights' },
  { key: 'includeRemixes', label: 'Remixes' },
  { key: 'includeVideoDate', label: 'Video date' },
  { key: 'includeCourtesyCredit', label: 'Courtesy credit' },
]

const EXTERNAL_TOGGLES = [
  { key: 'includeAlbumRights', label: 'Album rights' },
  { key: 'includeSingle', label: 'Single' },
  { key: 'includeCredit', label: 'Credit' },
  { key: 'includeCreditPhrase', label: 'Credit phrase' },
]

/**
 * Operations -> Waiver.
 *
 * Composes an exclusivity-waiver / side-artist-clearance letter client-side,
 * then either opens it as a mailto: draft or — when WAIVER_MSAL_CLIENT_ID is
 * configured — creates a real Outlook draft in the user's own mailbox via a
 * browser-acquired Graph token.
 */
export default function OpsWaiverPage() {
  const [waiverType, setWaiverType] = useState('encore')
  const [encoreValues, setEncoreValues] = useState(defaultEncoreValues)
  const [externalValues, setExternalValues] = useState(defaultExternalValues)
  const [encoreOptions, setEncoreOptions] = useState(defaultEncoreOptions)
  const [externalOptions, setExternalOptions] = useState(defaultExternalOptions)
  const [outlookConfig, setOutlookConfig] = useState(null)
  const [formError, setFormError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [busy, setBusy] = useState(false)

  const isExternal = waiverType === 'external'
  const values = isExternal ? externalValues : encoreValues
  const options = isExternal ? externalOptions : encoreOptions
  const setValues = isExternal ? setExternalValues : setEncoreValues
  const setOptions = isExternal ? setExternalOptions : setEncoreOptions
  const toggles = isExternal ? EXTERNAL_TOGGLES : ENCORE_TOGGLES

  useEffect(() => {
    let cancelled = false
    fetchWaiverOutlookConfig()
      .then((cfg) => {
        if (!cancelled) setOutlookConfig(cfg)
      })
      .catch(() => {
        if (!cancelled) {
          setOutlookConfig({ msalConfigured: false, attachAvailable: false })
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  const fields = useMemo(
    () => visibleFields(waiverType, options),
    [waiverType, options],
  )
  const missing = useMemo(
    () => missingRequiredFields(waiverType, values, options),
    [waiverType, values, options],
  )
  const body = useMemo(
    () => buildWaiverBody(waiverType, values, options),
    [waiverType, values, options],
  )
  const subject = useMemo(
    () => defaultSubject(waiverType, values),
    [waiverType, values],
  )

  const msalReady = Boolean(
    outlookConfig?.msalConfigured && outlookConfig?.msalClientId,
  )

  const setField = useCallback(
    (name, value) => {
      setValues((prev) => ({ ...prev, [name]: value }))
      setFormError(null)
      setNotice(null)
    },
    [setValues],
  )

  function guard() {
    if (missing.length) {
      setFormError(`Missing required: ${missing.map((f) => f.label).join(', ')}`)
      return false
    }
    return true
  }

  async function handleMailto() {
    if (!guard()) return
    setBusy(true)
    setNotice(null)
    try {
      const result = await openWaiverMailto({
        recipient: values.RecipientEmail,
        subject,
        body,
      })
      if (result?.opened) window.location.href = result.opened
      setNotice(
        result?.copiedBody
          ? 'Letter copied to the clipboard — the mail draft was too long for a mailto link, so paste it in.'
          : 'Opened your mail client.',
      )
    } catch (err) {
      setFormError(err?.message || String(err))
    } finally {
      setBusy(false)
    }
  }

  async function handleOutlookDraft() {
    if (!guard()) return
    setBusy(true)
    setFormError(null)
    setNotice(null)
    try {
      // MSAL runs in the browser; the Graph token never touches our server
      // except as the X-Graph-Token header on this one call.
      const { acquireWaiverGraphToken } = await import('../../utils/snoop/waiverMsal')
      const token = await acquireWaiverGraphToken({
        clientId: outlookConfig.msalClientId,
        tenantId: outlookConfig.msalTenantId,
        scopes: outlookConfig.scopes,
      })
      const res = await createWaiverOutlookDraft(token, {
        to: values.RecipientEmail,
        subject,
        body,
        waiverType,
        attachPaymentDocs: Boolean(outlookConfig?.attachAvailable),
      })
      const bits = ['Outlook draft created.']
      if (res?.attached?.length) bits.push(`Attached: ${res.attached.join(', ')}`)
      if (res?.attachError) bits.push(res.attachError)
      setNotice(bits.join(' '))
      if (res?.webLink) window.open(res.webLink, '_blank', 'noreferrer')
    } catch (err) {
      setFormError(err?.message || String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">
          Waiver
        </h1>
        <p className="text-sm text-gray-600">
          Compose an exclusivity waiver / side-artist clearance and send it as a
          draft.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5 shadow-sm">
          {WAIVER_TYPES.map((opt) => {
            const on = waiverType === opt.value
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  setWaiverType(opt.value)
                  setFormError(null)
                  setNotice(null)
                }}
                className={[
                  'rounded-md px-2.5 py-1.5 text-sm font-medium',
                  on ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50',
                ].join(' ')}
              >
                {opt.label}
              </button>
            )
          })}
        </div>
        {toggles.map((t) => (
          <label
            key={t.key}
            className="inline-flex items-center gap-2 text-sm text-gray-700"
          >
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300"
              checked={Boolean(options[t.key])}
              onChange={(e) => {
                setOptions((prev) => ({ ...prev, [t.key]: e.target.checked }))
                setNotice(null)
              }}
            />
            {t.label}
          </label>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-2 gap-3">
            {fields.map((f) => (
              <label
                key={f.name}
                className={f.inputType === 'date' ? '' : 'col-span-2'}
              >
                <span className={labelClass}>
                  {f.label}
                  {f.required === false ? null : (
                    <span className="ml-1 text-rose-500">*</span>
                  )}
                </span>
                <input
                  type={f.inputType === 'date' ? 'date' : 'text'}
                  className={inputClass}
                  value={values[f.name] ?? ''}
                  placeholder={f.placeholder || ''}
                  onChange={(e) => setField(f.name, e.target.value)}
                />
              </label>
            ))}
          </div>

          {formError ? (
            <p className="mt-3 text-sm text-red-600">{formError}</p>
          ) : null}
          {notice ? (
            <p className="mt-3 text-sm text-emerald-700">{notice}</p>
          ) : null}

          <div className="mt-4 flex flex-wrap gap-2">
            {msalReady ? (
              <button
                type="button"
                className={btnPrimary}
                disabled={busy}
                onClick={handleOutlookDraft}
              >
                {busy ? 'Working…' : 'Create Outlook draft'}
              </button>
            ) : null}
            <button
              type="button"
              className={msalReady ? btnSecondary : btnPrimary}
              disabled={busy}
              onClick={handleMailto}
            >
              {busy ? 'Working…' : 'Open in mail client'}
            </button>
          </div>
          {!msalReady ? (
            <p className="mt-2 text-xs text-gray-500">
              Opens your default mail client via mailto. Configure
              WAIVER_MSAL_CLIENT_ID for Outlook drafts with attachments.
            </p>
          ) : null}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Preview
          </p>
          <p className="mt-1 truncate text-sm font-semibold text-gray-900" title={subject}>
            {subject}
          </p>
          <pre className="mt-2 max-h-[32rem] overflow-auto whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-gray-700">
            {body}
          </pre>
        </div>
      </div>
    </div>
  )
}
